import { IPublicClientApplication } from "@azure/msal-browser";
import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Route, Routes, useNavigate, useMatch } from "react-router-dom";
import coPetenceLogo from "../../assets/coPetence_Logo_White.svg";
import { getAccessibleApps, getBoard, getMe, LinearState } from "../api/linearClient";
import { InternalHeaderContext } from "../InternalHeaderContext";
import { internalApiScopes } from "../msalConfig";
import { useTheme } from "../ThemeContext";
import styles from "../styles/internalRoot.module.scss";
import InternalBoardPage from "./InternalBoardPage";
import InternalIssuePage from "./InternalIssuePage";
import InternalSharePage from "./InternalSharePage";
import backlogIcon from "../../assets/backlog.svg";
import cancelledIcon from "../../assets/cancelled.svg";
import doneIcon from "../../assets/done.svg";
import extendedFeaturesIcon from "../../assets/extended-features.svg";
import inProgressIcon from "../../assets/in-progress.svg";
import inReviewIcon from "../../assets/in-review.svg";
import paperClipIcon from "../../assets/paperclip.svg";
import todoIcon from "../../assets/todo.svg";
import triageIcon from "../../assets/triage.svg";

function normalizeKey(value: string) {
	return value.trim().toLowerCase().replace(/\s+/g, " ");
}

function getStateIcon(state: LinearState): string | null {
	const key = normalizeKey(state.name || "");
	switch (key) {
		case "triage": return triageIcon;
		case "backlog": return backlogIcon;
		case "todo":
		case "to do": return todoIcon;
		case "in progress": return inProgressIcon;
		case "in review": return inReviewIcon;
		case "done": return doneIcon;
		case "cancelled":
		case "canceled":
		case "duplicate": return cancelledIcon;
		case "extended features": return extendedFeaturesIcon;
		case "paperclip": return paperClipIcon;
		default: return null;
	}
}

const STATE_ORDER: Record<string, number> = {
	"done": 0,
	"in review": 1,
	"in progress": 2,
	"todo": 3,
	"to do": 3,
	"triage": 4,
	"backlog": 5,
	"extended features": 6,
	"cancelled": 7,
	"canceled": 7,
	"duplicate": 7,
	"paperclip": 8,
};

function sortedStates(states: LinearState[]): LinearState[] {
	return [...states].sort((a, b) => {
		const orderA = STATE_ORDER[normalizeKey(a.name || "")] ?? 99;
		const orderB = STATE_ORDER[normalizeKey(b.name || "")] ?? 99;
		return orderA - orderB;
	});
}

type BoardSummary = { states: LinearState[]; counts: Record<string, number> };

function useBoardSummaries(
	instance: IPublicClientApplication,
	scopes: string[],
	appIds: string[],
) {
	const [summaries, setSummaries] = useState<Record<string, BoardSummary>>({});

	useEffect(() => {
		if (!appIds.length) return;
		let cancelled = false;
		(async () => {
			const results: Record<string, BoardSummary> = {};
			await Promise.all(
				appIds.map(async (id) => {
					try {
						const board = await getBoard(instance, scopes, id);
						const counts: Record<string, number> = {};
						for (const [stateId, issues] of Object.entries(board.issuesByState)) {
							counts[stateId] = issues.length;
						}
						results[id] = { states: board.states, counts };
					} catch { /* ignore per-app failures */ }
				}),
			);
			if (!cancelled) setSummaries(results);
		})();
		return () => { cancelled = true; };
	}, [instance, scopes, appIds.join(",")]);

	return summaries;
}

function InternalHome() {
	const { instance } = useMsal();
	const scopes = useMemo(() => internalApiScopes, []);
	const navigate = useNavigate();
	const [apps, setApps] = useState<{ id: string; name: string }[]>([]);
	const [error, setError] = useState<string | null>(null);
	const appIds = useMemo(() => apps.map((a) => a.id), [apps]);
	const boardSummaries = useBoardSummaries(instance, scopes, appIds);

	useEffect(() => {
		(async () => {
			try {
				const list = await getAccessibleApps(instance, scopes);
				setApps(list);
				if (list.length === 1) {
					navigate(`/internal/app/${list[0].id}`);
				}
			} catch (e: any) {
				setError(e?.message || String(e));
			}
		})();
	}, [instance, navigate, scopes]);

	return (
		<div className={styles.page}>
			<div className={styles.content}>
				{error ? <div className={styles.error}>{error}</div> : null}
				<h1 className={styles.h1}>Select application</h1>
				<div className={styles.appList}>
					{apps.map((a) => {
						const summary = boardSummaries[a.id];
						return (
							<Link key={a.id} className={styles.appLink} to={`/internal/app/${a.id}`}>
								<span className={styles.appName}>{a.name}</span>
								{summary && (
									<div className={styles.stateBadges}>
										{sortedStates(summary.states).map((state) => {
											const icon = getStateIcon(state);
											const count = summary.counts[state.id] ?? 0;
											if (!icon || count === 0) return null;
											const key = normalizeKey(state.name || "");
											return (
												<div
													key={state.id}
													className={styles.stateBadge}
													data-state-key={key}
													title={state.name}
												>
													<img src={icon} alt="" className={styles.stateBadgeIcon} />
													<span>{count}</span>
												</div>
											);
										})}
									</div>
								)}
							</Link>
						);
					})}
					{!error && apps.length === 0 ? <div className={styles.muted}>No access.</div> : null}
				</div>
			</div>
		</div>
	);
}

function InternalAppLayout({ children }: { children: React.ReactNode }) {
	const { instance, accounts } = useMsal();
	const { theme, toggleTheme } = useTheme();
	const issueMatch = useMatch("/internal/app/:appId/issue/:issueId")
	const issueId = issueMatch?.params.issueId
	const active = accounts[0];
	const scopes = useMemo(() => internalApiScopes, []);
	const [leftContent, setLeftContent] = useState<React.ReactNode>(null);
	const [titleContent, setTitleContent] = useState<React.ReactNode>(null);
	const [admin, setAdmin] = useState<boolean | null>(null);

	useEffect(() => {
		if (!accounts.length) return;
		getMe(instance, scopes)
			.then((data) => setAdmin(data.admin))
			.catch(() => setAdmin(false));
	}, [instance, scopes, accounts.length]);

	const contextValue = useMemo(() => ({
		setLeftContent,
		setTitleContent
	}), []);

	return (
		<InternalHeaderContext.Provider value={contextValue}>
			<div className={styles.root}>
				<div className={styles.topbar}>
					<div className={styles.topbarLeft}>
						{leftContent}
						<Link to="/internal" className={styles.topbarTitle}>
							{coPetenceLogo ? <img src={coPetenceLogo} alt="" /> : "coPetence"}
						</Link>
					</div>
					<div className={styles.topbarMiddle}>
						{titleContent}
					</div>
					<div className={styles.topbarRight}>
						{admin === true ? (
							<Link className={styles.button} id={styles.shareButton} to={`/internal/share${issueId ? `?id=${issueId}` : ""}`}>
								Share
							</Link>
						) : null}
						<button
							className={styles.themeToggle}
							onClick={toggleTheme}
							title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
							type="button"
						>
							{theme === "dark" ? (
								<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
									<circle cx="8" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.5" />
									<path d="M8 1.5v1.5M8 13v1.5M1.5 8H3M13 8h1.5M3.17 3.17l1.06 1.06M11.77 11.77l1.06 1.06M3.17 12.83l1.06-1.06M11.77 4.23l1.06-1.06" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
								</svg>
							) : (
								<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
									<path d="M14 9.68A6.5 6.5 0 0 1 6.32 2 6.5 6.5 0 1 0 14 9.68Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
								</svg>
							)}
						</button>
						<a
							className={styles.button}
							href="#"
							onClick={(e) => {
								e.preventDefault();
								instance.logoutRedirect({
									postLogoutRedirectUri: `${window.location.origin}/internal`,
								});
							}}
						>
							{active?.username}
						</a>
					</div>
				</div>
				<div className={styles.body}>{children}</div>
			</div>
		</InternalHeaderContext.Provider >
	);
}

function AuthGate({ children }: { children: React.ReactNode }) {
	const { instance, accounts, inProgress } = useMsal();
	const scopes = useMemo(() => internalApiScopes, []);
	const loginTriggered = useRef(false);
	const [loginError, setLoginError] = useState<string | null>(null);

	useEffect(() => {
		if (loginError) return;
		if (loginTriggered.current) return;
		if (accounts.length) return;
		if (inProgress !== InteractionStatus.None) return;

		loginTriggered.current = true;

		try {
			const result = instance.loginRedirect({ scopes });
			Promise.resolve(result).catch((e: any) => {
				setLoginError(e?.message || String(e));
			});
		} catch (e: any) {
			setLoginError(e?.message || String(e));
		}
	}, [accounts.length, inProgress, instance, loginError, scopes]);

	if (loginError) {
		return (
			<div className={styles.page}>
				<div className={styles.content}>
					<div className={styles.error}>Login failed: {loginError}</div>
					<div className={styles.muted}>
						Check `REACT_APP_AZURE_AD_CLIENT_ID` and `REACT_APP_AZURE_AD_AUTHORITY`.
					</div>
				</div>
			</div>
		);
	}

	if (!accounts.length) {
		return (
			<div className={styles.page}>
				<div className={styles.content}>
					<div className={styles.muted}>Redirecting to login…</div>
				</div>
			</div>
		);
	}

	return <>{children}</>;
}

function InternalAppShell() {
	return (
		<AuthGate>
			<InternalAppLayout>
				<Routes>
					<Route index element={<InternalHome />} />
					<Route path="share" element={<InternalSharePage />} />
					<Route path="app/:applicationId" element={<InternalBoardPage />}>
						<Route path="issue/:issueId" element={<InternalIssuePage />} />
					</Route>
				</Routes>
			</InternalAppLayout>
		</AuthGate>
	);
}

export default function InternalApp() {
	return <InternalAppShell />;
}
